<template>
  <div class="balance py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <!-- <RechargeCreate ref="rechargeCreate" /> -->
        <v-btn
          v-if="!isGeneratingReport"
          @click="fetchMovementReportGroupedByBranch"
          :disabled="isGeneratingReport"
          color="primary"
          elevation="0"
        >
            <v-progress-circular
              v-if="isGeneratingReport"
              :size="14"
              :width="2"
              color="grey"
              class="mr-2"
              indeterminate
            />
            <v-icon v-else left>mdi-download-box-outline</v-icon>
            <span class="d-none d-md-flex body-2">
              {{ $t('movement.actions.movementExcel.generateExcelGroupedByBranch') }}
            </span>
        </v-btn>

        <v-btn
          v-if="!isGeneratingReport"
          @click="fetchMovementReport"
          :disabled="isGeneratingReport"
          color="primary"
          elevation="0"
          class="ml-3"
        >
            <v-icon left>mdi-download-box-outline</v-icon>
            <span class="d-none d-md-flex body-2">
              {{ $t('movement.actions.movementExcel.generateExcel') }}
            </span>
        </v-btn>
        <v-btn
          v-if="isGeneratingReport"
          :disabled="isGeneratingReport"
          color="primary"
          elevation="0"
        >
          <v-progress-circular
            :size="14"
            :width="2"
            color="grey"
            class="mr-0"
            indeterminate/>
        </v-btn>
      </template>
    </h-header>

    <MovementListFilters />

<div class="mb-8">
  <p class="mb-1 subtitle-2 grey--text">
    {{ $t('movement.label.balance') }}
  </p>
  <div class="d-flex align-center" v-if="!moneyLoading">    
    <div class="mr-12">
      <div class="d-flex align-center">
        <p class="text-h10 grey--text mb-0 mr-2">
          {{ $t('movement.label.available') }}
        </p>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" small class="text-grey">
              mdi-help-circle
            </v-icon>
          </template>
          <span>{{ $t('movement.messages.balanceAvailableForUse') }}</span>        </v-tooltip>
      </div>
      <p class="text-h4 mb-0">
        <span :class="moneyColor(balance)">
          {{ availableBalance(balance) }}
        </span>
      </p>
    </div>
    <div>
      <div class="d-flex align-center">
        <p class="text-h10 grey--text mb-0 mr-2">
          {{ $t('movement.label.committed') }}
        </p>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" small class="text-grey">
              mdi-help-circle
            </v-icon>
          </template>
          <span>{{ $t('movement.messages.valueAlreadyCommittedToOpenAppointments') }}</span>
        </v-tooltip>
      </div>
      <p class="text-h4 mb-0 text-red" style="color: #D00000;">           
        <span>
          {{ availableBalance(committedBalance) }}
        </span>
      </p>
    </div>
  </div>
  <v-skeleton-loader v-else type="heading" class="mt-3 mb-10" />
</div>

    <v-card outlined>
      <v-data-table
        disable-sort
        hide-default-footer
        mobile-breakpoint="20"
        :headers="headers"
        :items="movements"
        :loading="isLoading"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
        @dblclick:row="showMovementRow"
        class="clickable"
      >
        <template v-slot:[`item.created_at`]="{ item }">
          {{ createdAt(item) }}
        </template>
        <template v-slot:[`item.description`]="{ item }">
          <span v-html="description(item)" />
        </template>
        <template v-slot:[`item.category`]="{ item }">
          <v-chip :color="category(item).color" small>
            <v-icon class="mr-1" x-small>{{ category(item).icon }}</v-icon>
            {{ category(item).label }}
          </v-chip>
        </template>
        <template v-slot:[`item.reason`]="{ item }">
          {{ reason(item) }}
        </template>
        <template v-slot:[`item.total`]="{ item }">
          <span v-if="item.total" :class="moneyColor(item.total)">
            {{ total(item) }}
          </span>
          <span v-else>---</span>
        </template>
      </v-data-table>
    </v-card>

    <h-pagination :fetch="fetchMovements" />

    <RightDrawer :more-info="false">
      <MovementServiceSummary :loading="loading" />
    </RightDrawer>
  </div>
</template>
<script>
import Loading from '@/components/app/Loading'
import RightDrawer from '@/components/app/RightDrawer'
import RechargeCreate from '@/components/recharge/RechargeCreate'
import MovementServiceSummary from '@/components/movement/MovementServiceSummary'
import MovementListFilters from '@/components/movement/MovementListFilters'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { movementHeaders } from './listHeaders/movementHeaders'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { handleParams } from '@/util/routes'
import router from '@/router'

export default {
  name: 'MovementList',

  components: {
    Loading,
    RightDrawer,
    RechargeCreate,
    MovementListFilters,
    MovementServiceSummary,
  },

  computed: {
    ...mapGetters([
      'user',
      'balance',
      'committedBalance',
      'movement',
      'isLoading',
      'movements',
      'itemsPerPage',
      'isGeneratingReport',
    ]),
  },

  methods: {
    ...mapActions([
      'fetchMovement',
      'fetchMovements',
      'showErrorMessage',
      'fetchCurrentRealEstate',
      'generateMovementReport',
      'fetchMovementReport',
      'fetchMovementReportGroupedByBranch',
    ]),
    ...mapMutations(['setIsDrawerOpen', 'setIsLoading']),

    showMovement(id) {
      this.loading = true
      this.setIsDrawerOpen(true)
      this.fetchMovement(id).finally(() => (this.loading = false))

      handleParams('movements', id)
    },

    showMovementRow(event, { item }) {
      this.showMovement(item?.id)
    },

    openCreateRechargeWithParams() {
      const params = this.$route.query

      this.$nextTick(() => {
        if (params.createRecharge === 'true')
          this.$refs.rechargeCreate.$refs.createModal.dialog = true
      })
    },

    moneyColor(amount) {
      if (!amount) return 'grey--text text--lighten-1'
      return amount > 0 ? 'green--text text--lighten-1' : 'red--text text--lighten-1'
    },

    availableBalance(amount) {
      return amount ? catalogMonetary(amount) : '---'
    },

    createdAt(item) {
      return item.created_at ? catalogDate(item.created_at) : '---'
    },

    description(item) {
      const description = item.description
      const rechargeComment = item.recharge_comment

      if (description && rechargeComment) return `
          <h6 class="mt-1 text-uppercase grey--text">${this.$t('movement.label.comment')}</h6>
          <p class="mb-2">${description}</p>
          <h6 class="text-uppercase grey--text">${this.$t('movement.label.rechargeNote')}</h6>
          <p class="mb-1">${rechargeComment}</p>
        `

        if (description) return description
    },

    category(item) {
      return item.category === 'RECHARGE' ? this.rechargeObj : this.expenditureObj
    },

    reason(item) {
      return item.reason ? this.$t(`movement.reason.${item.reason}`) : '---'
    },

    total(item) {
      return item.total ? catalogMonetary(item.total) : '---'
    },
  },

  data() {
    return {
      headers: movementHeaders,
      moneyLoading: false,
      loading: false,

      rechargeObj: {
        icon: 'mdi-arrow-down',
        color: 'green lighten-4',
        label: this.$t('movement.category.RECHARGE'),
      },

      expenditureObj: {
        icon: 'mdi-arrow-up',
        color: 'red lighten-4',
        label: this.$t('movement.category.EXPENDITURE'),
      },
    }
  },

  mounted() {
    this.moneyLoading = true

    const movementId = router.currentRoute.query.id
    movementId && this.showMovement(movementId)

    this.fetchMovements().then(() => {
      this.fetchCurrentRealEstate()
        .finally(() => (this.moneyLoading = false))
    })

    this.openCreateRechargeWithParams()
  },
}
</script>
